package org.catharsis.spendtel.feature.presentation.web

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowBack
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material.icons.outlined.AutoAwesome
import androidx.compose.material.icons.outlined.Lock
import androidx.compose.material.icons.outlined.Psychology
import androidx.compose.material.icons.outlined.Timeline
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavHostController
import org.catharsis.TVTally.theme.deepGreen
import org.catharsis.spendtel.core.navigation.AppRoutes
import org.catharsis.spendtel.feature.presentation.auth.AuthFormController
import org.catharsis.spendtel.feature.presentation.auth.AuthLoadingOverlay
import org.catharsis.spendtel.feature.presentation.auth.AuthMessageBanner
import org.catharsis.spendtel.feature.presentation.auth.rememberAuthFormController
import org.catharsis.spendtel.feature.presentation.auth.webAuthFieldColors
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.feature.presentation.legal.LegalAcceptanceCheckbox
import org.catharsis.spendtel.ui.LegalLinks

private val formMaxWidth = 420.dp

@Composable
fun WebAuthenticationScreen(navHostController: NavHostController) {
    val controller = rememberAuthFormController(navHostController)

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.White)
            .imePadding(),
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            val useSplitLayout = maxWidth >= 960.dp

            if (useSplitLayout) {
                Row(modifier = Modifier.fillMaxSize()) {
                    WebAuthBrandPanel(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight(),
                    )
                    WebAuthFormPanel(
                        controller = controller,
                        navHostController = navHostController,
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight(),
                    )
                }
            } else {
                Column(modifier = Modifier.fillMaxSize()) {
                    WebAuthBrandPanel(
                        modifier = Modifier.fillMaxWidth(),
                        compact = true,
                    )
                    WebAuthFormPanel(
                        controller = controller,
                        navHostController = navHostController,
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxWidth(),
                    )
                }
            }
        }

        if (controller.authState.isLoading) {
            AuthLoadingOverlay()
        }
    }
}

@Composable
private fun WebAuthBrandPanel(
    modifier: Modifier = Modifier,
    compact: Boolean = false,
) {
    Box(
        modifier = modifier
            .background(SpendTelGradients.hero)
            .then(
                if (compact) {
                    Modifier.padding(horizontal = 24.dp, vertical = 32.dp)
                } else {
                    Modifier.padding(48.dp)
                },
            ),
    ) {
        if (!compact) {
            Box(
                modifier = Modifier
                    .size(220.dp)
                    .align(Alignment.TopEnd)
                    .offset(x = 40.dp, y = (-20).dp)
                    .clip(CircleShape)
                    .background(Color.White.copy(alpha = 0.06f)),
            )
        }

        Column(
            modifier = if (compact) Modifier.fillMaxWidth() else Modifier.align(Alignment.CenterStart),
            verticalArrangement = Arrangement.spacedBy(if (compact) 12.dp else 24.dp),
        ) {
            Text(
                text = "SPENDTEL",
                style = MaterialTheme.typography.labelMedium,
                color = Color.White.copy(alpha = 0.72f),
                letterSpacing = 2.sp,
            )

            Text(
                text = if (compact) {
                    "Your spending story awaits"
                } else {
                    "Turn bank statements into stories you'll actually read."
                },
                style = if (compact) {
                    MaterialTheme.typography.headlineSmall
                } else {
                    MaterialTheme.typography.displaySmall
                },
                fontWeight = FontWeight.Bold,
                color = Color.White,
                lineHeight = if (compact) 32.sp else 44.sp,
            )

            if (!compact) {
                Text(
                    text = "Upload a PDF, get personality insights, category trends, and merchant highlights — in about two minutes.",
                    style = MaterialTheme.typography.bodyLarge,
                    color = Color.White.copy(alpha = 0.88f),
                    lineHeight = 28.sp,
                )

                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    WebAuthValueRow(
                        icon = Icons.Outlined.AutoAwesome,
                        title = "AI narratives",
                        body = "Readable stories, not spreadsheet dumps.",
                    )
                    WebAuthValueRow(
                        icon = Icons.Outlined.Psychology,
                        title = "Spending personality",
                        body = "Patterns and traits without the judgment.",
                    )
                    WebAuthValueRow(
                        icon = Icons.Outlined.Timeline,
                        title = "Month-over-month",
                        body = "See how your habits evolve over time.",
                    )
                }

                Surface(
                    shape = RoundedCornerShape(16.dp),
                    color = Color.White.copy(alpha = 0.1f),
                ) {
                    Column(
                        modifier = Modifier.padding(20.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        Text(
                            text = "\"Finally understood where my money went — and it didn't feel like getting lectured.\"",
                            color = Color.White.copy(alpha = 0.92f),
                            lineHeight = 24.sp,
                            fontStyle = FontStyle.Italic,
                        )
                        Text(
                            text = "— Early SpendTel user",
                            style = MaterialTheme.typography.labelMedium,
                            color = Color.White.copy(alpha = 0.65f),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun WebAuthValueRow(
    icon: ImageVector,
    title: String,
    body: String,
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(14.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Surface(
            shape = RoundedCornerShape(12.dp),
            color = Color.White.copy(alpha = 0.12f),
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = Color.White,
                modifier = Modifier.padding(10.dp),
            )
        }
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(
                text = title,
                fontWeight = FontWeight.SemiBold,
                color = Color.White,
            )
            Text(
                text = body,
                style = MaterialTheme.typography.bodyMedium,
                color = Color.White.copy(alpha = 0.78f),
            )
        }
    }
}

@Composable
private fun WebAuthFormPanel(
    controller: AuthFormController,
    navHostController: NavHostController,
    modifier: Modifier = Modifier,
) {
    val scrollState = rememberScrollState()

    Box(
        modifier = modifier.background(Color.White),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .widthIn(max = formMaxWidth)
                .fillMaxWidth()
                .verticalScroll(scrollState)
                .padding(horizontal = 32.dp, vertical = 40.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp),
        ) {
            Row(
                modifier = Modifier
                    .clickable { navHostController.navigate(AppRoutes.Landing.name) }
                    .padding(vertical = 4.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Outlined.ArrowBack,
                    contentDescription = null,
                    tint = SpendTelReportColors.inkMuted,
                    modifier = Modifier.size(18.dp),
                )
                Text(
                    text = "Back to home",
                    style = MaterialTheme.typography.bodyMedium,
                    color = SpendTelReportColors.inkMuted,
                    fontWeight = FontWeight.Medium,
                )
            }

            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    text = if (controller.isRegisterMode) "Create your account" else "Welcome back",
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.ink,
                )
                Text(
                    text = if (controller.isRegisterMode) {
                        "Start building your spending story in minutes."
                    } else {
                        "Sign in to view your reports and upload new statements."
                    },
                    style = MaterialTheme.typography.bodyLarge,
                    color = SpendTelReportColors.inkMuted,
                    lineHeight = 24.sp,
                )
            }

            if (controller.isRegisterMode) {
                LegalAcceptanceCheckbox(
                    checked = controller.acceptedTerms,
                    onCheckedChange = controller.onAcceptedTermsChange,
                    onPrivacyClick = {
                        navHostController.navigate(AppRoutes.PrivacyPolicy.name)
                    },
                    onTermsClick = {
                        navHostController.navigate(AppRoutes.TermsAndConditions.name)
                    },
                )
            }

            OutlinedButton(
                onClick = controller.onGoogleSignIn,
                enabled = !controller.authState.isLoading && controller.canUseGoogleSignIn,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(50.dp),
                shape = RoundedCornerShape(10.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    containerColor = Color.White,
                    contentColor = SpendTelReportColors.ink,
                ),
                border = ButtonDefaults.outlinedButtonBorder(enabled = true).copy(
                    brush = Brush.linearGradient(listOf(Color(0xFFE2E8F0), Color(0xFFE2E8F0))),
                ),
            ) {
                Text(
                    text = "G",
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    modifier = Modifier.padding(end = 10.dp),
                )
                Text(
                    text = "Continue with Google",
                    fontWeight = FontWeight.SemiBold,
                )
            }

            WebAuthDivider(label = "or continue with email")

            OutlinedTextField(
                value = controller.email,
                onValueChange = controller.onEmailChange,
                modifier = Modifier.fillMaxWidth(),
                label = { Text("Email address") },
                leadingIcon = {
                    Icon(Icons.Default.Email, contentDescription = null, tint = SpendTelReportColors.inkMuted)
                },
                singleLine = true,
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Email,
                    imeAction = ImeAction.Next,
                ),
                shape = RoundedCornerShape(10.dp),
                colors = webAuthFieldColors(),
            )

            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                OutlinedTextField(
                    value = controller.password,
                    onValueChange = controller.onPasswordChange,
                    modifier = Modifier.fillMaxWidth(),
                    label = { Text("Password") },
                    leadingIcon = {
                        Icon(Icons.Default.Lock, contentDescription = null, tint = SpendTelReportColors.inkMuted)
                    },
                    trailingIcon = {
                        IconButton(onClick = controller.onPasswordVisibilityToggle) {
                            Icon(
                                imageVector = if (controller.passwordVisible) {
                                    Icons.Default.VisibilityOff
                                } else {
                                    Icons.Default.Visibility
                                },
                                contentDescription = null,
                            )
                        }
                    },
                    singleLine = true,
                    visualTransformation = if (controller.passwordVisible) {
                        VisualTransformation.None
                    } else {
                        PasswordVisualTransformation()
                    },
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Password,
                        imeAction = if (controller.isRegisterMode) ImeAction.Next else ImeAction.Done,
                    ),
                    keyboardActions = KeyboardActions(
                        onDone = { if (!controller.isRegisterMode) controller.submit() },
                    ),
                    shape = RoundedCornerShape(10.dp),
                    colors = webAuthFieldColors(),
                )

                if (!controller.isRegisterMode) {
                    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.CenterEnd) {
                        TextButton(onClick = controller.onForgotPassword) {
                            Text(
                                text = "Forgot password?",
                                color = SpendTelReportColors.violet,
                                fontWeight = FontWeight.Medium,
                            )
                        }
                    }
                }
            }

            if (controller.isRegisterMode) {
                OutlinedTextField(
                    value = controller.confirmPassword,
                    onValueChange = controller.onConfirmPasswordChange,
                    modifier = Modifier.fillMaxWidth(),
                    label = { Text("Confirm password") },
                    leadingIcon = {
                        Icon(Icons.Default.Lock, contentDescription = null, tint = SpendTelReportColors.inkMuted)
                    },
                    singleLine = true,
                    visualTransformation = if (controller.passwordVisible) {
                        VisualTransformation.None
                    } else {
                        PasswordVisualTransformation()
                    },
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Password,
                        imeAction = ImeAction.Done,
                    ),
                    keyboardActions = KeyboardActions(onDone = { controller.submit() }),
                    shape = RoundedCornerShape(10.dp),
                    colors = webAuthFieldColors(),
                )
            }

            controller.errorMessage?.let { message ->
                AuthMessageBanner(message = message, isError = true)
            }

            controller.infoMessage?.let { message ->
                AuthMessageBanner(message = message, isError = false)
            }

            Button(
                onClick = controller.submit,
                enabled = !controller.authState.isLoading && controller.canSubmit,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(50.dp),
                shape = RoundedCornerShape(10.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = deepGreen,
                    contentColor = Color.White,
                ),
            ) {
                Text(
                    text = if (controller.isRegisterMode) "Create account" else "Sign in",
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 16.sp,
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = if (controller.isRegisterMode) {
                        "Already have an account?"
                    } else {
                        "Don't have an account?"
                    },
                    color = SpendTelReportColors.inkMuted,
                )
                TextButton(
                    onClick = {
                        controller.switchMode(!controller.isRegisterMode)
                    },
                ) {
                    Text(
                        text = if (controller.isRegisterMode) "Sign in" else "Create one",
                        color = SpendTelReportColors.violet,
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(Color(0xFFF8FAFC))
                    .padding(14.dp),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    imageVector = Icons.Outlined.Lock,
                    contentDescription = null,
                    tint = deepGreen,
                    modifier = Modifier.size(20.dp),
                )
                Text(
                    text = "Statements are encrypted and only used to generate your report.",
                    style = MaterialTheme.typography.bodySmall,
                    color = SpendTelReportColors.inkMuted,
                    lineHeight = 18.sp,
                )
            }

            LegalLinks(
                onPrivacyClick = {
                    navHostController.navigate(AppRoutes.PrivacyPolicy.name)
                },
                onTermsClick = {
                    navHostController.navigate(AppRoutes.TermsAndConditions.name)
                },
            )
        }
    }
}

@Composable
private fun WebAuthDivider(label: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        HorizontalDivider(modifier = Modifier.weight(1f), color = Color(0xFFE2E8F0))
        Text(
            text = label,
            modifier = Modifier.padding(horizontal = 12.dp),
            style = MaterialTheme.typography.bodySmall,
            color = SpendTelReportColors.inkMuted,
        )
        HorizontalDivider(modifier = Modifier.weight(1f), color = Color(0xFFE2E8F0))
    }
}
